import { useEffect, useRef, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { abrirImpressao } from '@/comuns/impressao';
import { dataEHoraPorExtenso, dataSimples } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import type { CabecalhoDaClinica } from '@/data/fichaImpressa';
import { dados } from '@/data/porta';
import type { ArquivoDaFicha, CampoDeFicha, FichaPreenchida } from '@/data/tipos';
import { TextoFormatado } from '@/screens/fichas/TextoFormatado';
import { atual } from '@/session/sessao';
import { respostaEmTexto } from './FichaClinica';

/* No papel a folha perde a moldura da tela; o que quebra página mal (seção,
   assinatura) fica inteiro. */
const ESTILO_DA_FOLHA = '@page{size:A4;margin:12mm}'
    + '.folha{width:auto!important;margin:0!important;border:0!important;border-radius:0!important;padding:0!important;box-shadow:none!important}'
    + '.secao,.assinatura,.identificacao{break-inside:avoid;page-break-inside:avoid}';

/** Campos que ocupam a largura toda da folha: texto longo não cabe em meia coluna. */
const LARGOS: CampoDeFicha['tipo'][] = ['texto-longo', 'texto-rico', 'tabela', 'arquivo'];

export interface ImpressaoDaFichaModalProps {
    /** Nulo quando o `?ficha=` da URL não é deste prontuário. */
    ficha: FichaPreenchida | null;
    paciente: string;
    nascimento?: string;
    onFechar: () => void;
}

function Rotulo({ children }: { children: string }) {
    return (
        <dt style={{ fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
            {children}
        </dt>
    );
}

/**
 * A ficha preenchida numa folha A4 — imprimir e "Salvar como PDF" são o mesmo
 * diálogo do navegador, então os dois botões levam ao mesmo documento.
 *
 * O papel sai do MESMO desenho da pré-visualização: montar outro HTML para
 * imprimir faria a folha divergir do que a pessoa conferiu na tela. Os rótulos
 * são os da cópia do modelo do dia (regra inviolável 4).
 */
export function ImpressaoDaFichaModal({ ficha, paciente, nascimento, onFechar }: ImpressaoDaFichaModalProps) {
    const aviso = useAviso();
    const folhaRef = useRef<HTMLDivElement>(null);
    const [cabecalho, setCabecalho] = useState<CabecalhoDaClinica | null>(null);
    const [erroDoCabecalho, setErroDoCabecalho] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        dados().cabecalhoDaClinica()
            .then((c) => { if (vivo) setCabecalho(c); })
            .catch((e: unknown) => { if (vivo) setErroDoCabecalho(e instanceof Error ? e.message : 'Não foi possível ler os dados da clínica.'); });
        return () => { vivo = false; };
    }, []);

    const imprimir = () => {
        if (!ficha || !folhaRef.current) return;
        const abriu = abrirImpressao({ titulo: `${ficha.nome} · ${paciente}`, corpo: folhaRef.current.outerHTML, estilo: ESTILO_DA_FOLHA, rodape: false });
        if (!abriu) aviso({ title: 'Permita pop-ups para imprimir', description: 'O navegador bloqueou a janela de impressão.', tone: 'warning' });
    };

    const nomeDaClinica = cabecalho?.nome ?? atual()?.clinica ?? 'Clínica';
    const campos = ficha?.definicaoUsada;
    const corrigida = ficha ? new Date(ficha.atualizadoEm).getTime() - new Date(ficha.criadoEm).getTime() > 60_000 : false;

    return (
        <Modal
            open width={800} onClose={onFechar}
            title={ficha?.nome ?? 'Ficha preenchida'}
            description={ficha ? `Preenchida em ${dataEHoraPorExtenso(ficha.criadoEm)}${ficha.preenchidaPor ? ` por ${ficha.preenchidaPor}` : ''}` : undefined}
            footer={(
                <>
                    {ficha && (
                        <span style={{ marginRight: 'auto', maxWidth: 300, textAlign: 'left', fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)' }}>
                            No diálogo de impressão, escolha “Salvar como PDF” para gerar o arquivo.
                        </span>
                    )}
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    {ficha && (
                        <>
                            <Button variant="secondary" iconLeft={<Icon name="printer" size={15} />} onClick={imprimir}>Imprimir</Button>
                            <Button iconLeft={<Icon name="file-down" size={15} />} onClick={imprimir}>Gerar PDF</Button>
                        </>
                    )}
                </>
            )}
        >
            {!ficha ? (
                <Alert tone="danger" title="Ficha não encontrada" dismissible={false}>
                    O endereço aponta para uma ficha que não está neste prontuário.
                </Alert>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {erroDoCabecalho && (
                        <Alert tone="warning" title="O cabeçalho sai só com o nome da clínica" dismissible={false}>{erroDoCabecalho}</Alert>
                    )}
                    <div style={{ background: 'var(--surface-secondary)', margin: '0 -22px', padding: '18px 22px', maxHeight: '58vh', overflowY: 'auto' }}>
                        <div
                            ref={folhaRef} className="folha"
                            style={{
                                maxWidth: 720, margin: '0 auto', background: '#fff', padding: '28px 32px 30px',
                                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--font-ui)', color: 'var(--text-primary)',
                            }}
                        >
                            <header style={{ display: 'flex', alignItems: 'flex-start', gap: 16, paddingBottom: 14, borderBottom: '2px solid var(--burgundy-700)' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{nomeDaClinica}</p>
                                    {cabecalho && (cabecalho.razaoSocial || cabecalho.documento) && (
                                        <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: '16px', color: 'var(--text-secondary)' }}>
                                            {[cabecalho.razaoSocial, cabecalho.documento ? `CNPJ/CPF ${cabecalho.documento}` : ''].filter(Boolean).join(' · ')}
                                        </p>
                                    )}
                                    {cabecalho?.endereco && (
                                        <p style={{ margin: '2px 0 0', fontSize: 12, lineHeight: '16px', color: 'var(--text-secondary)' }}>{cabecalho.endereco}</p>
                                    )}
                                </div>
                                <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                                    <p style={{ margin: 0, fontSize: 12, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Ficha de atendimento</p>
                                    <p style={{ margin: '3px 0 0', fontSize: 14, fontWeight: 600 }}>{ficha.nome}</p>
                                </div>
                            </header>

                            <section className="identificacao" style={{ margin: '16px 0 0', padding: 12, background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xs)' }}>
                                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: '6px 14px', fontSize: 12.5 }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Paciente</dt>
                                    <dd style={{ margin: 0, fontWeight: 600 }}>{paciente}</dd>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Nascimento</dt>
                                    <dd style={{ margin: 0, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{nascimento ? dataSimples(nascimento) : '—'}</dd>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Preenchida em</dt>
                                    <dd style={{ margin: 0, fontWeight: 600 }}>{dataEHoraPorExtenso(ficha.criadoEm)}</dd>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Profissional</dt>
                                    <dd style={{ margin: 0, fontWeight: 600 }}>{ficha.preenchidaPor ?? '—'}</dd>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Modelo</dt>
                                    <dd style={{ margin: 0, fontWeight: 600 }}>{ficha.versaoDoModelo !== undefined ? `versão ${ficha.versaoDoModelo}` : '—'}</dd>
                                    <dt style={{ color: 'var(--text-secondary)' }}>Correção</dt>
                                    <dd style={{ margin: 0, fontWeight: 600 }}>{corrigida ? dataEHoraPorExtenso(ficha.atualizadoEm) : '—'}</dd>
                                </dl>
                            </section>

                            {!campos ? (
                                <section className="secao" style={{ marginTop: 18 }}>
                                    <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--warning-strong)' }}>
                                        Ficha anterior à cópia do modelo: as respostas saem como foram gravadas, sem os rótulos do dia.
                                    </p>
                                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px 20px' }}>
                                        {Object.entries(ficha.respostas).map(([chave, valor]) => (
                                            <div key={chave}>
                                                <Rotulo>{chave}</Rotulo>
                                                <dd style={{ margin: '2px 0 0', fontSize: 13, lineHeight: '18px', whiteSpace: 'pre-wrap' }}>{typeof valor === 'object' ? JSON.stringify(valor) : String(valor ?? '—')}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </section>
                            ) : (
                                <section className="secao" style={{ marginTop: 18 }}>
                                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                                        {campos.map((campo) => {
                                            const valor = ficha.respostas[campo.uid];
                                            return (
                                                <div key={campo.uid} style={{ gridColumn: LARGOS.includes(campo.tipo) ? '1 / -1' : 'auto' }}>
                                                    <Rotulo>{campo.rotulo}</Rotulo>
                                                    <dd style={{ margin: '2px 0 0', fontSize: 13, lineHeight: '18px', whiteSpace: campo.tipo === 'texto-rico' ? 'normal' : 'pre-wrap' }}>
                                                        {campo.tipo === 'texto-rico' && valor
                                                            ? <TextoFormatado texto={String(valor)} />
                                                            : campo.tipo === 'arquivo'
                                                                /* No papel o anexo é o nome dele: o link assinado vence. */
                                                                ? ((Array.isArray(valor) ? valor : []) as ArquivoDaFicha[]).map((a) => a.nome).join(', ') || '—'
                                                                : respostaEmTexto(campo, valor)}
                                                    </dd>
                                                </div>
                                            );
                                        })}
                                    </dl>
                                </section>
                            )}

                            <div className="assinatura" style={{ marginTop: 34, display: 'flex', gap: 40, alignItems: 'flex-end' }}>
                                {[
                                    [ficha.preenchidaPor ?? 'Profissional', 'Profissional responsável'],
                                    [paciente, 'Paciente ou responsável legal'],
                                ].map(([nome, papel]) => (
                                    <div key={papel} style={{ flex: 1, borderTop: '1px solid var(--neutral-700)', paddingTop: 5 }}>
                                        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{nome}</p>
                                        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}>{papel}</p>
                                    </div>
                                ))}
                            </div>

                            <footer style={{ marginTop: 22, paddingTop: 9, borderTop: '1px solid var(--border)', display: 'flex', gap: 14, fontSize: 12, lineHeight: '16px', color: 'var(--text-tertiary)' }}>
                                <span style={{ flex: 1 }}>{[nomeDaClinica, cabecalho?.telefone, cabecalho?.email].filter(Boolean).join(' · ')}</span>
                                <span style={{ textAlign: 'right', flex: '0 0 auto' }}>Documento gerado pela Fórmula MED · emitido em {new Date().toLocaleDateString('pt-BR')}</span>
                            </footer>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}
